/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { CommonImage } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import DOMPurify from "isomorphic-dompurify";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { Suspense, useState } from "react";
import { findLandingPageForPost } from "@/utils/blogLandingBridge.util";
import { formatDate } from "@/utils";

// Dynamically imported so its formik/yup validation chain lands in its own
// on-demand chunk instead of the shared bundle every route requests - a
// static import here was showing up in chunks fetched even on pages that
// never render a blog post (e.g. the homepage).
const ConsultForm = dynamic(
  () => import("./components/consultForm/ConsultForm"),
);

interface TProps {
  poster: any;
  title: string;
  subtitles: Array<string>;
  content: string;
  tags: Array<string>;
  createdAt?: string;
}

interface Heading {
  id: string;
  title: string;
}

function processHtmlContent(htmlContent: string): {
  modifiedHtml: string;
  headings: Heading[];
} {
  const headings: Heading[] = [];

  const modifiedHtml = htmlContent.replace(
    /<h2(.*?)>(.*?)<\/h2>/g,
    (match: string, existingAttributes: string, content: string): string => {
      const id = content
        .replace(/<[^>]*>/g, "")
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");

      headings.push({
        id: id,
        title: content.replace(/<[^>]*>/g, ""),
      });

      return `<h2 id="${id}"${existingAttributes}>${content}</h2>`;
    },
  );

  return { modifiedHtml, headings };
}

export default function BlogPostSection({
  poster,
  title,
  content,
  tags,
  createdAt,
}: TProps) {
  const html = DOMPurify.sanitize(content);
  const { modifiedHtml, headings } = processHtmlContent(html);
  const [activeId, setActiveId] = useState<string>(headings[0]?.id);
  // Bridges the blog and landing-page silos, which previously had 0 links
  // in either direction - see blogLandingBridge.util.ts.
  const relatedLandingPage = findLandingPageForPost(title, tags);

  const handleLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    targetId: string,
  ) => {
    event.preventDefault();
    setActiveId(targetId);
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      const elementPosition = targetElement.getBoundingClientRect().top;

      const currentScrollY = window.pageYOffset;
      const y = elementPosition + currentScrollY - 80;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
      window.history.pushState(null, "", `#${targetId}`);
    }
  };

  return (
    <section>
      <div className="scontainer mx-auto flex flex-col w-full items-start gap-8 px-8 pt-10">
        <Link href="/blogs" replace className="md:hidden">
          <ArrowLeft color="black" className="p-0" />
        </Link>
        <Breadcrumb className="hidden md:block">
          <BreadcrumbList>
            <BreadcrumbItem>
              <Link href="/blogs">Blog</Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <Suspense
          fallback={
            <Skeleton className="w-full h-[400px] bg-muted-2-foreground relative" />
          }
        >
          <CommonImage
            width={1920}
            height={1080}
            className="w-full md:h-[520px] object-cover lg:mb-6"
            alt={title}
            src={poster?.at(0)?.url}
            priority={true}
          />
        </Suspense>

        <h1 className="text-3xl md:text-4xl font-bold">{title}</h1>
        {createdAt && (
          <p className="text-sm text-muted-2-foreground">
            By Fydo Team <span aria-hidden="true">&middot;</span>{" "}
            <time dateTime={new Date(createdAt).toISOString()}>
              {formatDate(createdAt, "dd mmm yyyy")}
            </time>
          </p>
        )}

        <div className="flex flex-col w-full">
          <div className="flex flex-col lg:flex-row md:gap-16 w-full">
            <div className="w-full md:min-w-[360px] lg:w-64 flex-shrink-0">
              <div className="w-full md:min-w-[360px] lg:w-64 flex-shrink-0 sticky top-24">
                {headings?.length > 0 && (
                  <>
                    <h2 className="text-xl font-semibold">In this article</h2>
                    <nav className="flex flex-col py-4 mt-1">
                      {headings
                        ?.filter((heading) => Object.keys(heading).length > 0)
                        ?.map(
                          (heading, index) =>
                            heading.title !== "" && (
                              <a
                                key={index}
                                href={"#" + heading.id}
                                onClick={(event) =>
                                  handleLinkClick(event, heading.id)
                                }
                                className={`flex items-center text-[16px] border-primary font-semibold text-ellipsis hover:text-primary py-2 pl-2 ${activeId === heading.id ? "text-primary border-l-3" : "text-secondary-6"}`}
                              >
                                {heading.title}
                              </a>
                            ),
                        )}
                    </nav>
                  </>
                )}
                <ConsultForm className="hidden lg:block mt-8" />
              </div>
            </div>

            <div className="flex-1">
              <Card className="border-none shadow-none pt-0">
                <CardContent className="p-0 flex flex-col gap-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-3">
                      <div
                        dangerouslySetInnerHTML={{ __html: modifiedHtml }}
                        className="flex flex-col gap-3 [&_h2]:text-2xl [&_h2]:border-none [&_h2]:font-medium [&_h2:not(:first-of-type)]:mt-8 [&_h2:not(:first-of-type)]:mb-0 [&_p]:text-base [&_p]:font-host-grotesk [&_ul]:pl-[18px] [&_ul]:list-disc"
                      />
                    </div>
                  </div>
                  <div className="w-full flex items-center flex-wrap gap-2 border-t pt-4">
                    {tags.map((t: string) => (
                      <Badge
                        key={t}
                        className="py-1 md:p-1 md:px-2 "
                        variant="outline"
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                  {relatedLandingPage && (
                    <Link
                      href={relatedLandingPage.path}
                      className="group flex items-center justify-between gap-3 rounded-xl border border-border bg-white p-5 transition-colors hover:border-primary"
                    >
                      <span className="font-medium text-secondary">
                        {relatedLandingPage.label}
                      </span>
                      <ArrowUpRight className="size-5 shrink-0 text-muted-2-foreground transition-colors group-hover:text-primary" />
                    </Link>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          <ConsultForm className="lg:hidden" />
        </div>
      </div>
    </section>
  );
}
